import React, { useMemo, useEffect } from "react";
import moment from "moment";
// Consumer Drawer Content

// Mui
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { useState } from "react";
import { IconButton, Grid2 as Grid } from "@mui/material";
import { ArrowBackIos, Add, Close } from "@mui/icons-material";

// Mui
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";

// Custom
import DateAndTimePopover from "../popovers/DateAndTimePopover";
import CustomToast from "../toast/CustomToast";
import BackdropSpinner from "../spinner/BackdropSpinner";
import BillingDetailsModal from "../modals/BillingDetailsModal";
import ContainedButton from "../../forms/button/ContainedButton";

// React icons
import { FaArrowUp } from "react-icons/fa";
import { FaArrowDown } from "react-icons/fa";
import { FaArrowRight } from "react-icons/fa";

// Svg
import Calendar2 from "../../assets/svg/calendar2";

// Redux
import {
  setTransactionsHistory,
  setBillingDeterminants,
  setBillingData,
  setSelectedBillingData,
  setSelectedBillingDeterminant,
  setSelectedMeter,
  setBalance,
} from "../../redux/features/BillingDeterminantSlice";

// Utils
import { fetchGETRequest } from "../../utils/services";
import {
  getTimeStamp,
  formatTime,
  formatDate,
  isUUID,
} from "../../utils/helper";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  BillingDeterminationItem,
  ToastState,
  TransactionItem,
} from "../../types";
import { UnifiedIcon } from "../Icon/UnifiedIcon";

interface ConsumerBillingDrawerContentProps {
  onClose: () => void;
}

const ConsumerBillingDrawerContent: React.FC<
  ConsumerBillingDrawerContentProps
> = ({ onClose }) => {
  const [value, setValue] = useState<string>("2");

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ p: 1 }}>
      <Stack
        display={"flex"}
        flexDirection={"row"}
        alignItems={"center"}
        gap={1}
        p={2}
      >
        <IconButton
          sx={{
            p: 0.5,
          }}
          onClick={onClose}
        >
          <ArrowBackIos style={{ fontSize: 18, color: "black" }} />
        </IconButton>
        <Stack
          flexDirection={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          flex={1}
        >
          <Stack flexDirection={"row"} gap={1}>
            <Typography
              variant="inherit"
              fontSize={"20px"}
              fontWeight={700}
              color="#1064ED"
            >
              Consumer Id :
            </Typography>
            <Typography
              variant="inherit"
              color="#1064ED"
              fontWeight={700}
              fontSize={"20px"}
            >
              {"1234567890"}
            </Typography>
          </Stack>
          {/* <Button
            sx={{
              textTransform: "none",
              backgroundColor: "red",
              color: "white",
              borderRadius: 2,
            }}
          >
            Meter Disconnect
          </Button> */}
        </Stack>
      </Stack>

      <Box sx={{ width: "100%", typography: "body1" }}>
        <TabContext value={value}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <TabList
              onChange={handleChange}
              aria-label="lab API tabs example"
              sx={{ height: 30, minHeight: 30 }}
              scrollButtons="auto"
              allowScrollButtonsMobile
              variant="scrollable"
            >
              <Tab
                label="Wallet Balance"
                value="2"
                sx={{ textTransform: "none", minHeight: 28, height: 28 }}
              />

              <Tab
                label="Billing History"
                value="3"
                sx={{ textTransform: "none", minHeight: 28, height: 28 }}
              />
            </TabList>
          </Box>

          <Box sx={{ display: value === "2" ? "block" : "none", p: 0 }}>
            <ConsumerDrawerBillAndPayment meterId={"1234567890"} />
          </Box>

          <Box sx={{ display: value === "3" ? "block" : "none", p: 0 }}>
            <ConsumerBillingDeterminants />
          </Box>
        </TabContext>
      </Box>
    </Box>
  );
};

export default ConsumerBillingDrawerContent;

const ConsumerDrawerBillAndPayment = ({ meterId }: { meterId: string }) => {
  const [toast, setToast] = useState<ToastState>({
    message: "",
    severity: "info",
  });
  const [loader, setLoader] = useState(false);
  const [showPaymentOptions, setShowPaymentOptions] = useState<boolean>(false);
  const [selectedAmount, setSelectedAmount] = useState<string>("");
  const [quickAddAmount, setQuickAddAmount] = useState<number | null>(null);
  const [amountError, setAmountError] = useState<string>("");
  const [payLoading, setPayLoading] = useState(false);
  const dispatch = useAppDispatch();

  const { balance, transactionsHistory } = useAppSelector(
    (state) => state.billing,
  );

  useEffect(() => {
    dispatch(setSelectedMeter(meterId));
  }, [meterId]);

  const TransactionHistoryCard = (
    id: number | string,
    animate: boolean,
    amount: number,
    timestamp: string,
    index: number,
  ) => {
    const isPositive = amount > 0;
    return (
      <Box
        sx={{
          background: isPositive ? "#e1f5d7" : "#f7cdcb",
          borderLeftColor: isPositive ? "#9CE976" : "#FF3B30",
        }}
        className="wallet_balance_transaction_history_row"
        key={index}
      >
        <Stack direction={"row"} alignItems={"center"} gap={"5px"}>
          <UnifiedIcon
            icon={isPositive ? FaArrowUp : FaArrowDown}
            color={isPositive ? "#9CE976" : "#FF3B30"}
            fontSize="15px"
          />
          <Typography variant="body2">{Number(amount).toFixed(2)}</Typography>
        </Stack>
        <Typography variant="body2">
          {formatDate(timestamp, "DD-MM-YYYY HH:mm:ss")}
        </Typography>
      </Box>
    );
  };

  const quickAddOptions = [500, 1000, 1500, 2000, 5000];
  const MIN_AMOUNT = 100;
  const MAX_AMOUNT = 50000;

  const handleQuickAddClick = (amount: number) => {
    setQuickAddAmount(amount);
    setSelectedAmount("");
    setAmountError("");
  };

  const handleCustomAmountChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    // Allow only numbers
    if (value === "" || /^\d+$/.test(value)) {
      setSelectedAmount(value);
      setQuickAddAmount(null);
      // Validate in real-time
      if (value !== "") {
        const numValue = Number(value);
        if (numValue < MIN_AMOUNT) {
          setAmountError(`Minimum amount is ₹${MIN_AMOUNT}`);
        } else if (numValue > MAX_AMOUNT) {
          setAmountError(`Maximum amount is ₹${MAX_AMOUNT}`);
        } else {
          setAmountError("");
        }
      } else {
        setAmountError("");
      }
    }
  };

  const validateAmount = (amount: number): boolean => {
    if (amount < MIN_AMOUNT) {
      setAmountError(`Minimum amount is ₹${MIN_AMOUNT}`);
      return false;
    }
    if (amount > MAX_AMOUNT) {
      setAmountError(`Maximum amount is ₹${MAX_AMOUNT}`);
      return false;
    }
    setAmountError("");
    return true;
  };

  const handlePayClick = async () => {
    const amountToAdd =
      quickAddAmount !== null ? quickAddAmount : Number(selectedAmount);

    if (!amountToAdd || amountToAdd === 0) {
      setToast({
        message: "Please select or enter an amount",
        severity: "error",
      });
      return;
    }

    if (!validateAmount(amountToAdd)) {
      return;
    }

    setPayLoading(true);
    try {
      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const newBalance = balance + amountToAdd;
      const newTransaction: TransactionItem = {
        id: transactionsHistory?.length + 1,
        amount: amountToAdd,
        timestamp: new Date().toISOString(),
        animate: true,
      };

      dispatch(setBalance(newBalance));
      dispatch(
        setTransactionsHistory([...transactionsHistory, newTransaction]),
      );

      setToast({
        message: `Successfully added ₹${amountToAdd.toFixed(2)} to wallet`,
        severity: "success",
      });

      // Reset form
      setSelectedAmount("");
      setQuickAddAmount(null);
      setAmountError("");
      // Optionally hide payment options after successful payment
      setShowPaymentOptions(false);
    } catch (error) {
      setToast({
        message: "Failed to add money. Please try again.",
        severity: "error",
      });
    } finally {
      setPayLoading(false);
    }
  };

  const getFinalAmount = (): number => {
    return quickAddAmount !== null
      ? quickAddAmount
      : Number(selectedAmount) || 0;
  };

  const isPayButtonDisabled = (): boolean => {
    const amount = getFinalAmount();
    return (
      amount === 0 || amount < MIN_AMOUNT || amount > MAX_AMOUNT || payLoading
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <BackdropSpinner open={loader} />
      <CustomToast
        open={toast?.message}
        severity={toast?.severity}
        setToast={setToast}
      />

      <Grid container spacing={2.25}>
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          <Card
            sx={{
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
              borderWidth: "1px",
              borderColor: "#E0E0E0",
            }}
          >
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                flexGrow: 1,
                p: 3,
              }}
            >
              {/* Wallet Balance Section and Add Money Button */}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ py: 2, width: "100%" }}
                gap={2}
              >
                <Stack alignItems="center" gap={2}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#666666",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Wallet Balance
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontSize: "36px",
                      fontWeight: 700,
                      color: balance >= 0 ? "#9CE976" : "#FF3B30",
                      lineHeight: 1.2,
                    }}
                  >
                    &#8377; {Number(balance).toFixed(2)}
                  </Typography>
                </Stack>
                {/* Add Money Toggle Button - Show when payment options are hidden */}
                {!showPaymentOptions && (
                  <ContainedButton
                    label="Add Money"
                    sx={{
                      width: "100%",
                      maxWidth: "300px",
                      height: "45px",
                      color: "#ffffff",
                      fontSize: "16px",
                      fontWeight: 600,
                    }}
                    onClick={() => setShowPaymentOptions(true)}
                  />
                )}
              </Stack>

              {/* Add Money Section - Show when showPaymentOptions is true */}
              {showPaymentOptions && (
                <>
                  <Divider />
                  <Stack direction={"column"} gap={2.5}>
                    <Stack
                      direction={"row"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                    >
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        sx={{ fontSize: "16px" }}
                      >
                        Add Money
                      </Typography>
                      <IconButton
                        onClick={() => setShowPaymentOptions(false)}
                        sx={{
                          color: "#666666",
                          "&:hover": {
                            backgroundColor: "#F5F5F5",
                            color: "#181B25",
                          },
                        }}
                      >
                        <Close />
                      </IconButton>
                    </Stack>

                    {/* Quick Add Buttons */}
                    <Stack
                      direction={"row"}
                      gap={1.5}
                      sx={{ flexWrap: "wrap" }}
                    >
                      {quickAddOptions.map((amount) => (
                        <Button
                          key={amount}
                          onClick={() => handleQuickAddClick(amount)}
                          variant={
                            quickAddAmount === amount ? "contained" : "outlined"
                          }
                          sx={{
                            minWidth: "100px",
                            height: "40px",
                            borderRadius: "8px",
                            textTransform: "none",
                            fontSize: "14px",
                            fontWeight: 600,
                            borderColor: "#E0E0E0",
                            color:
                              quickAddAmount === amount ? "#ffffff" : "#181B25",
                            backgroundColor:
                              quickAddAmount === amount
                                ? "linear-gradient(180deg, #1976d2 0%, #0396FF 100%)"
                                : "transparent",
                            "&:hover": {
                              backgroundColor:
                                quickAddAmount === amount
                                  ? "linear-gradient(180deg, #75D3FB 0%, #28A6FF 100%)"
                                  : "#F5F5F5",
                              borderColor: "#1976d2",
                            },
                          }}
                        >
                          &#8377; {amount}
                        </Button>
                      ))}
                    </Stack>

                    {/* Custom Amount Input */}
                    <Stack direction={"column"} gap={1}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "13px",
                          fontWeight: 500,
                          color: "#666666",
                        }}
                      >
                        Or enter custom amount
                      </Typography>
                      <TextField
                        placeholder="Enter amount"
                        value={selectedAmount}
                        onChange={handleCustomAmountChange}
                        error={!!amountError}
                        helperText={amountError}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography
                                sx={{
                                  fontSize: "16px",
                                  fontWeight: 600,
                                  color: "#181B25",
                                }}
                              >
                                &#8377;
                              </Typography>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            background: "#fcfcfc",
                            "& fieldset": {
                              border: amountError
                                ? "2px solid #FF3B30"
                                : "1px solid #c3c3c3",
                            },
                            "&:hover fieldset": {
                              border: "2px solid #1976d2",
                            },
                            "&.Mui-focused fieldset": {
                              border: "2px solid #1976d2",
                            },
                            "& input": {
                              padding: "12px 10px",
                              color: "#181B25",
                              fontWeight: 500,
                              fontSize: "15px",
                              "&::placeholder": {
                                color: "#B0B0B0",
                                opacity: 1,
                              },
                            },
                          },
                          "& .MuiFormHelperText-root": {
                            marginLeft: 0,
                            fontSize: "12px",
                          },
                        }}
                      />
                    </Stack>

                    {/* Pay Button */}
                    <ContainedButton
                      label="Pay"
                      sx={{
                        width: "100%",
                        height: "45px",
                        color: "#ffffff",
                        fontSize: "16px",
                        fontWeight: 600,
                        mt: 1,
                      }}
                      loading={payLoading}
                      disabled={isPayButtonDisabled()}
                      onClick={handlePayClick}
                    />
                  </Stack>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          <Card
            className="wallet_balance_transaction_history"
            sx={{
              marginTop: "20px",
              height: "434px",
              overflow: "auto",
              borderWidth: "1px",
              borderColor: "#E0E0E0",
            }}
          >
            <CardHeader
              title={
                <Typography variant="body1" fontWeight={"600"}>
                  Transaction History
                </Typography>
              }
            />
            <Divider />
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "15px",
                overflow: "auto",
              }}
            >
              {transactionsHistory?.map(
                (item: TransactionItem, index: number) => {
                  return TransactionHistoryCard(
                    item.id,
                    item.animate,
                    item.amount,
                    item.timestamp,
                    index,
                  );
                },
              )}

              {transactionsHistory?.length === 0 && (
                <Stack direction={"row"} justifyContent={"center"}>
                  <Typography variant="body1">No Data found</Typography>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export const ConsumerBillingDeterminants = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [timeRange, setTimeRange] = useState<number[]>([0, 1439]);
  const [rechargeAmount, setRechargeAmount] = useState("");
  const [toast, setToast] = useState<ToastState>({
    message: "",
    severity: "info",
  });
  const [loader, setLoader] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const dispatch = useAppDispatch();

  const {
    balance,
    transactionsHistory,
    workOrderHistory,
    billingDeterminants,
    billingData,
    selectedBillingDeterminant,
    selectedBillingData,
    selectedMeter,
    walletBalance,
    workOrderId,
  } = useAppSelector((state) => state.billing);

  const handleBillingDeterminant = async () => {
    if (!selectedMeter) {
      setToast({
        message: "Please Enter the meter serial No.",
        severity: "error",
      });
      setAnchorEl(null);
      return;
    }

    if (!isUUID(selectedMeter)) {
      setToast({
        message: "Please Enter the valid meter serial No.",
        severity: "error",
      });
      setAnchorEl(null);
      return;
    }
    // const deviceId = "30d33fa0-bb60-11ef-85d5-e5b1b2e5df91"
    const startTs = getTimeStamp(formatTime(timeRange[0]), new Date(startDate));
    const endTs = getTimeStamp(formatTime(timeRange[1]), new Date(endDate));
    const keys = "IP_KWH,EventCode";
    const intervalType = "WEEK";

    const eventsResponse: any = await fetchGETRequest(
      `devices/${selectedMeter}/events`,
    );

    let penaltyCount = 0;
    if (eventsResponse?.data && Array.isArray(eventsResponse.data)) {
      penaltyCount = eventsResponse.data.filter((event: any) => {
        const eventTs = moment(event.CreatedAt).valueOf();
        return (
          event.Command === "METER_COVER_OPEN" &&
          eventTs >= startTs &&
          eventTs <= endTs
        );
      }).length;
    }

    const response: any = await fetchGETRequest(
      `plugins/telemetry/DEVICE/${selectedMeter}/getBillingDeterminants?keys=${keys}&startTs=${startTs}&endTs=${endTs}&intervalType=${intervalType}`,
    );

    if (response?.IP_KWH && response?.IP_KWH?.length > 0) {
      const values = response?.IP_KWH?.map((item: { value: string }) =>
        parseFloat(item.value),
      );
      const max = Math.max(...values);
      const min = Math.min(...values);
      const totalCost = max - min;

      const newBalance = balance - totalCost;

      const newBillingDeterminant: BillingDeterminationItem = {
        id: billingDeterminants?.length + 1,
        fromDate: formatDate(startTs, "YYYY-MM-DDTHH:mm"),
        toDate: formatDate(endTs, "YYYY-MM-DDTHH:mm"),
        timestamp: new Date().toISOString(),
      };

      const newTransaction: TransactionItem = {
        id: transactionsHistory?.length + 1,
        amount: -totalCost,
        timestamp: new Date().toISOString(),
        animate: true,
      };

      dispatch(
        setTransactionsHistory([...transactionsHistory, newTransaction]),
      );

      dispatch(
        setBillingDeterminants([...billingDeterminants, newBillingDeterminant]),
      );

      const newBillingData = {
        id: newBillingDeterminant.id,
        seqNo: billingDeterminants.length + 1,
        registerType: selectedMeter,
        qu: max.toFixed(2),
        activeEnergy: "1.0.1.8.0.255",
        ut: min.toFixed(2),
        qt: totalCost.toFixed(2),
        penaltyCount,
      };

      dispatch(setBillingData([...billingData, newBillingData]));
      // localStorage.setItem(`billingData_${newBillingDeterminant.id}`, JSON.stringify(newBillingData));
      // dispatch(setBalance(newBalance));
      setStartDate(new Date());
      setEndDate(new Date());
      setTimeRange([0, 1439]);

      // if (newBalance < 20) {
      //   // await createWorkOrder('disconnect');
      //   setToast({ message: "Wallet balance is below 20. Disconnect work order created.", severity: "error" });
      // }
    } else {
      setStartDate(new Date());
      setEndDate(new Date());
      setTimeRange([0, 1439]);
      setToast({
        message: "No billing data found for the selected time range.",
        severity: "error",
      });
    }

    setAnchorEl(null);
  };

  const GenerateBillingDeterminantCard = (
    item: BillingDeterminationItem,
    index: number,
  ) => {
    return (
      <Box
        className="wallet_balance_billing_determinant_row"
        sx={{
          backgroundColor: "#e3f1fa",
          "&:hover": {
            backgroundColor: "#e0f7fa",
          },
          cursor: "pointer",
        }}
        onClick={() => {
          const savedBillingData = billingData?.filter(
            (bill: any) => bill?.id === item?.id,
          );
          dispatch(setSelectedBillingData(savedBillingData));
          dispatch(setSelectedBillingDeterminant(item));
          setOpenModal(true);
        }}
        key={index}
      >
        <Typography variant="body2" fontWeight={"500"}>
          Billing Determinant:{" "}
          {formatDate(new Date(item?.fromDate), "DD/MM/YYYY HH:mm:ss")}{" "}
          <Typography component="span">
            <UnifiedIcon icon={FaArrowRight} color="#181B25" fontSize="10px" />
          </Typography>{" "}
          {formatDate(new Date(item?.toDate), "DD/MM/YYYY HH:mm:ss")}{" "}
        </Typography>
        <Typography variant="body2" fontWeight={400}>
          {formatDate(item?.timestamp, "DD-MM-YYYY HH:mm:ss")}
        </Typography>
      </Box>
    );
  };

  const handleDateChange = (dates: [Date, Date]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const handleTimeRangeChange = (timeRange: number | number[]) => {
    if (Array.isArray(timeRange)) {
      setTimeRange(timeRange);
    } else {
      setTimeRange([timeRange, timeRange]);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <BackdropSpinner open={loader} />
      <CustomToast
        open={toast?.message}
        severity={toast?.severity}
        setToast={setToast}
      />
      <Card
        sx={{
          marginTop: "20px",
          overflow: "auto",
          height: "270px",
          borderWidth: "1px",
          borderColor: "#E0E0E0",
        }}
      >
        <CardHeader
          title={
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography variant="body1" fontWeight={"600"}>
                Generate Billing Determinant
              </Typography>
              <IconButton
                onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                  setAnchorEl(event.currentTarget)
                }
              >
                <Calendar2 />
              </IconButton>
            </Stack>
          }
        />
        <Divider />

        <CardContent
          sx={{ display: "flex", flexDirection: "column", gap: "15px" }}
        >
          {billingDeterminants?.map(
            (item: BillingDeterminationItem, index: number) => {
              return GenerateBillingDeterminantCard(item, index);
            },
          )}

          {billingDeterminants?.length === 0 && (
            <Stack direction={"row"} justifyContent={"center"}>
              <Typography variant="body1">No Data found</Typography>
            </Stack>
          )}

          <DateAndTimePopover
            anchorEl={anchorEl}
            setAnchorEl={(anchorEl: HTMLElement | null) =>
              setAnchorEl(anchorEl)
            }
            startDate={startDate}
            endDate={endDate}
            timeRange={timeRange}
            setTimeRange={handleTimeRangeChange}
            handleDateChange={handleDateChange}
            handleDateSubmit={handleBillingDeterminant}
            btnText={"Generate Billing Determinant"}
          />
          <BillingDetailsModal
            openModal={openModal}
            setOpenModal={setOpenModal}
            selectedBillingData={selectedBillingData}
            selectedBillingDeterminant={selectedBillingDeterminant}
            setSelectedBillingDeterminant={() => {
              dispatch(setSelectedBillingDeterminant);
            }}
          />
        </CardContent>
      </Card>
    </Box>
  );
};
